// IndexedDB storage via Dexie. Stores events + snapshot blobs.
// Export as a zip bundles events.jsonl + snapshots/ to mirror the Python
// EventWriter layout.

import Dexie, { type Table } from "dexie";
import JSZip from "jszip";

import { eventToJsonRecord, type AlertEvent } from "../inference/types";

interface EventRecord {
  event_id: string;
  json: ReturnType<typeof eventToJsonRecord>;
  receivedAt: number;
}

interface SnapshotRecord {
  event_id: string;
  blob: Blob;
}

class EventDB extends Dexie {
  events!: Table<EventRecord, string>;
  snapshots!: Table<SnapshotRecord, string>;

  constructor() {
    super("marine-detect-events");
    this.version(1).stores({
      events: "event_id, receivedAt",
      snapshots: "event_id",
    });
  }
}

const db = new EventDB();

export type StorageBackend = "indexeddb" | "memory" | "off";

/** Prototype guard: cap how much we keep on the device. Older events get evicted. */
export const MAX_EVENTS = 10;

export class EventStore {
  private backend: StorageBackend;
  private memoryEvents: EventRecord[] = [];
  private memorySnapshots: Map<string, Blob> = new Map();

  constructor(backend: StorageBackend = "indexeddb") {
    this.backend = backend;
  }

  setBackend(backend: StorageBackend): void {
    this.backend = backend;
  }

  async addEvent(ev: AlertEvent, snapshot?: Blob): Promise<void> {
    if (this.backend === "off") return;
    const json = eventToJsonRecord(ev);
    if (snapshot !== undefined) {
      json.snapshot_path = `snapshots/${ev.event_id}.jpg`;
    }
    const record: EventRecord = { event_id: ev.event_id, json, receivedAt: Date.now() };
    if (this.backend === "memory") {
      this.memoryEvents.push(record);
      if (snapshot) this.memorySnapshots.set(ev.event_id, snapshot);
      await this.evictExcess();
      return;
    }
    await db.events.put(record);
    if (snapshot) await db.snapshots.put({ event_id: ev.event_id, blob: snapshot });
    await this.evictExcess();
  }

  /** Drop oldest events past MAX_EVENTS. Snapshots tag along with their event. */
  private async evictExcess(): Promise<void> {
    if (this.backend === "memory") {
      while (this.memoryEvents.length > MAX_EVENTS) {
        const oldest = this.memoryEvents.shift();
        if (oldest) this.memorySnapshots.delete(oldest.event_id);
      }
      return;
    }
    if (this.backend !== "indexeddb") return;
    const total = await db.events.count();
    if (total <= MAX_EVENTS) return;
    const toRemove = total - MAX_EVENTS;
    const oldest = await db.events.orderBy("receivedAt").limit(toRemove).toArray();
    const ids = oldest.map((r) => r.event_id);
    await db.events.bulkDelete(ids);
    await db.snapshots.bulkDelete(ids);
  }

  async count(): Promise<number> {
    if (this.backend === "memory") return this.memoryEvents.length;
    if (this.backend === "off") return 0;
    return db.events.count();
  }

  async listEvents(limit = 50): Promise<EventRecord[]> {
    if (this.backend === "memory") {
      return this.memoryEvents.slice(-limit).reverse();
    }
    if (this.backend === "off") return [];
    const all = await db.events.orderBy("receivedAt").reverse().limit(limit).toArray();
    return all;
  }

  async getSnapshot(eventId: string): Promise<Blob | undefined> {
    if (this.backend === "memory") return this.memorySnapshots.get(eventId);
    if (this.backend === "off") return undefined;
    const r = await db.snapshots.get(eventId);
    return r?.blob;
  }

  async clear(): Promise<void> {
    this.memoryEvents = [];
    this.memorySnapshots.clear();
    if (this.backend === "indexeddb") {
      await db.events.clear();
      await db.snapshots.clear();
    }
  }

  async exportZip(): Promise<Blob> {
    const events = await this.listEvents(100000);
    const zip = new JSZip();
    const jsonl = events
      .slice()
      .reverse() // oldest first in the file
      .map((r) => JSON.stringify(r.json))
      .join("\n");
    zip.file("events.jsonl", jsonl);
    const folder = zip.folder("snapshots");
    if (folder) {
      for (const r of events) {
        const snap = await this.getSnapshot(r.event_id);
        if (snap) folder.file(`${r.event_id}.jpg`, snap);
      }
    }
    return zip.generateAsync({ type: "blob" });
  }
}
